import React, { useEffect } from 'react';
import './App.css';
import './styles/ErrorHandling.css';
import './styles/Feedback.css';
import './styles/ErrorBoundary.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { Amplify } from 'aws-amplify';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import awsConfig from './aws-exports';
import SearchAssetsPage from './pages/SearchAssetsPage';
import AssetDetailPage from './pages/AssetDetailPage';
import AssetEditPage from './pages/AssetEditPage';
import AssetReplaceFilePage from './pages/AssetReplaceFilePage';
import TaxonomyPage from './pages/TaxonomyPage';
import MainLayout from './components/layout/MainLayout';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { FeedbackProvider } from './contexts/FeedbackContext';
import FeedbackDisplay from './components/feedback/FeedbackDisplay';
import ErrorBoundary from './components/error/ErrorBoundary';
import GlobalErrorHandler from './components/common/GlobalErrorHandler';
import StagingBanner from './components/common/StagingBanner';
import TaxonomyInitProvider from './components/providers/TaxonomyInitProvider';
import { TaxonomySyncProvider } from './components/providers/TaxonomySyncProvider';
import { environmentSafeLog } from './utils/environment';
import { setupGlobalTaxonomyErrorHandler } from './utils/taxonomyErrorRecovery';
import { setupApiInterceptors } from './utils/apiInterceptor';
import RegisterAssetPageWrapper from './components/asset/RegisterAssetPageWrapper';
import CreateVariantPage from './pages/CreateVariantPage';

Amplify.configure(awsConfig);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { authStatus } = useAuthenticator();

  if (authStatus === 'configuring') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (authStatus !== 'authenticated') {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

const RootRedirect = () => {
  const { authStatus } = useAuthenticator();

  if (authStatus === 'configuring') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (authStatus === 'authenticated') {
    return <Navigate to="/search-assets" replace />;
  }

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiresAt');
    localStorage.removeItem('userInfo');

    const currentHostname = window.location.hostname;
    const lastHostname = sessionStorage.getItem('nna-last-hostname');

    if (lastHostname && lastHostname !== currentHostname) {
      sessionStorage.removeItem('nna-url-normalization-session');
    }

    sessionStorage.setItem('nna-last-hostname', currentHostname);

    environmentSafeLog('[APP] Setting up URL normalization system');
    setupApiInterceptors();

    environmentSafeLog('[APP] Setting up global taxonomy error handler');
    setupGlobalTaxonomyErrorHandler();
  }, []);

  return (
    <ErrorBoundary>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ErrorProvider>
            <GlobalErrorHandler />
            <StagingBanner />
            <FeedbackProvider>
              <NotificationsProvider>
                <Authenticator>
                  <TaxonomyInitProvider
                    fallback={
                      <div className="taxonomy-loading">
                        <div className="loading-spinner"></div>
                        <p>Loading taxonomy data...</p>
                      </div>
                    }
                  >
                    <TaxonomySyncProvider enableDebugLogging={process.env.NODE_ENV === 'development'}>
                      <Router>
                        <Routes>
                          <Route element={<MainLayout />}>
                            <Route
                              path="/register-asset"
                              element={
                                <ProtectedRoute>
                                  <RegisterAssetPageWrapper />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/search-assets"
                              element={
                                <ProtectedRoute>
                                  <SearchAssetsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/taxonomy"
                              element={
                                <ProtectedRoute>
                                  <TaxonomyPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/assets/:baseAssetId/create-variant"
                              element={
                                <ProtectedRoute>
                                  <CreateVariantPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/assets/:id"
                              element={
                                <ProtectedRoute>
                                  <AssetDetailPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/assets/:id/edit"
                              element={
                                <ProtectedRoute>
                                  <AssetEditPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/assets/:id/edit-file"
                              element={
                                <ProtectedRoute>
                                  <AssetReplaceFilePage />
                                </ProtectedRoute>
                              }
                            />
                            <Route path="/" element={<RootRedirect />} />
                            <Route path="*" element={<Navigate to="/search-assets" replace />} />
                          </Route>
                        </Routes>
                      </Router>
                      <FeedbackDisplay />
                    </TaxonomySyncProvider>
                  </TaxonomyInitProvider>
                </Authenticator>
              </NotificationsProvider>
            </FeedbackProvider>
          </ErrorProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ErrorBoundary>
  );
};

export default App;
